import { useState, useEffect } from "react";
import Loader from "components/Loader";
import MobileSupportTabBar from "components/TabBars/MobileSupportTabBar";
import HelpCard from "components/Cards/HelpCard";
import { Button, Col, Form } from "react-bootstrap";
import Validator from "utils/Validator";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import { getInputFieldStyle, getButtonStyle, getTextareaStyle } from "utils/formStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";

export default function TagsPage() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultGot, setIsResultGot] = useState(false);
  const [inputText, setInputText] = useState("");
  const [inputType, setInputType] = useState("keyword"); // "keyword" or "content"
  const [formData, setFormData] = useState({
    content: "",
    type: "keyword",
  });
  const [showSearchItem, setShowSearchItem] = useState("");
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [mockHashtags, setMockHashtags] = useState([]);

  const [errors, setErrors] = useState({ inputText: "" });
  const [tabs, setTabs] = useState([]);
  const firstLevelTopic = "SOCIAL";
  const secondLevelTopic = "META + INSTA";
  const thirdLevelTopic = "Tags";

  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
    setInsufficientCreditsData(null);
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  const handleInputTypeChange = (e) => {
    setInputType(e.target.value);
    setInputText(""); // Clear input when switching types
    setErrors({ inputText: "" }); // Clear errors
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    let error = "";
    if (name === "inputText") {
      if (!inputText.trim()) {
        error =
          inputType === "keyword"
            ? "Please enter a keyword"
            : "Please enter content";
      }
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleReset = () => {
    setShowSearchItem("");
    setIsResultGot(false);
    setInputText("");
    setInputType("keyword"); // Reset to default
    setShowErrorNotification(false);
    setErrorMessage("");
    setShowSuccessNotification(false);
    setSuccessMessage("");
    setErrors({ inputText: "" });
    setMockHashtags([]);
  };

  // Function to handle copying with success notification
  const handleCopyToClipboard = async (
    text,
    message = "Hashtag copied to clipboard!"
  ) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccessMessage(message);
      setShowSuccessNotification(true);
    } catch (err) {
      console.error("Failed to copy: ", err);
      setErrorMessage("Failed to copy to clipboard");
      setShowErrorNotification(true);
    }
  };

  // Mock hashtag generation function
  const generateMockHashtags = (input) => {
    const baseHashtags = [
      "#socialmedia",
      "#digitalmarketing",
      "#contentcreation",
      "#marketing",
      "#branding",
      "#socialmediamarketing",
      "#instagram",
      "#facebook",
      "#engagement",
      "#viral",
      "#trending",
      "#content",
      "#strategy",
      "#business",
      "#entrepreneur",
      "#success",
      "#motivation",
      "#inspiration",
      "#creative",
      "#design",
      "#photography",
      "#video",
      "#reels",
      "#stories",
    ];

    // Generate hashtags based on input
    const inputWords = input
      .toLowerCase()
      .split(/\s+/)
      .filter((word) => word.length > 2);
    const generatedHashtags = [];

    // Add input-based hashtags
    inputWords.forEach((word) => {
      generatedHashtags.push(`#${word}`);
      generatedHashtags.push(`#${word}marketing`);
      generatedHashtags.push(`#${word}content`);
    });

    // Mix with base hashtags
    const allHashtags = [...new Set([...generatedHashtags, ...baseHashtags])];

    // Return random selection of 20-30 hashtags
    const shuffled = allHashtags.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, Math.floor(Math.random() * 11) + 20); // 20-30 hashtags
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setShowSearchItem(inputText);

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Generate mock hashtags
      const hashtags = generateMockHashtags(inputText);
      setMockHashtags(hashtags);

      setTabs([
        {
          label: "Trending Tags",
          element: (
            <div className="p-3">
              <div className="mb-3">
                <h5>Generated Hashtags ({hashtags.length})</h5>
                <p className="text-muted">
                  Copy and paste these hashtags to boost your social media reach
                </p>
              </div>
              <div className="hashtag-container">
                {hashtags.map((tag, index) => (
                  <span
                    key={index}
                    className="badge bg-primary me-2 mb-2 p-2"
                    style={{ fontSize: "0.9rem", cursor: "pointer" }}
                    onClick={() =>
                      handleCopyToClipboard(
                        tag,
                        `"${tag}" copied to clipboard!`
                      )
                    }
                    title="Click to copy"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const allTags = hashtags.join(" ");
                    handleCopyToClipboard(
                      allTags,
                      `All ${hashtags.length} hashtags copied to clipboard!`
                    );
                  }}
                >
                  Copy All Tags
                </Button>
              </div>
            </div>
          ),
        },
        {
          label: "Popular Mix",
          element: (
            <div className="p-3">
              <div className="mb-3">
                <h5>Popular Hashtag Mix</h5>
                <p className="text-muted">
                  A curated selection of popular hashtags for maximum engagement
                </p>
              </div>
              <div className="hashtag-container">
                {hashtags.slice(0, 15).map((tag, index) => (
                  <span
                    key={index}
                    className="badge bg-success me-2 mb-2 p-2"
                    style={{ fontSize: "0.9rem", cursor: "pointer" }}
                    onClick={() =>
                      handleCopyToClipboard(
                        tag,
                        `"${tag}" copied to clipboard!`
                      )
                    }
                    title="Click to copy"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-3">
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() => {
                    const selectedTags = hashtags.slice(0, 15).join(" ");
                    handleCopyToClipboard(
                      selectedTags,
                      `15 popular hashtags copied to clipboard!`
                    );
                  }}
                >
                  Copy Selected Tags
                </Button>
              </div>
            </div>
          ),
        },
        {
          label: "Niche Specific",
          element: (
            <div className="p-3">
              <div className="mb-3">
                <h5>Niche Specific Tags</h5>
                <p className="text-muted">
                  Targeted hashtags for your specific content niche
                </p>
              </div>
              <div className="hashtag-container">
                {hashtags.slice(10, 25).map((tag, index) => (
                  <span
                    key={index}
                    className="badge bg-warning text-dark me-2 mb-2 p-2"
                    style={{ fontSize: "0.9rem", cursor: "pointer" }}
                    onClick={() =>
                      handleCopyToClipboard(
                        tag,
                        `"${tag}" copied to clipboard!`
                      )
                    }
                    title="Click to copy"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-3">
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => {
                    const nicheTags = hashtags.slice(10, 25).join(" ");
                    handleCopyToClipboard(
                      nicheTags,
                      `15 niche-specific hashtags copied to clipboard!`
                    );
                  }}
                >
                  Copy Niche Tags
                </Button>
              </div>
            </div>
          ),
        },
      ]);
      setIsResultGot(true);
    } catch (error) {
      console.error("Submission error:", error);
      setErrorMessage(
        error.message || "Failed to generate hashtags. Please try again."
      );
      setShowErrorNotification(true);
      setIsResultGot(false);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    let isValid = inputText.trim().length > 0;
    if (isValid) {
      setFormData({
        content: inputText,
        type: inputType,
      });
    }
    setIsFormValid(isValid);
  }, [inputText, inputType]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      {/* Toast Notifications */}
      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />
      <ToastAlert
        show={showSuccessNotification}
        message={successMessage}
        type="success"
        icon="fas fa-check-circle"
        duration={3000}
        onClose={() => setShowSuccessNotification(false)}
        position="top-right"
      />
      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {thirdLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / {secondLevelTopic} / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Optimizing hashtags on Meta platforms, including Instagram and Facebook,
        for a given keyword amplifies content discoverability through
        algorithmic recommendations and search functionalities, fostering higher
        engagement rates that indirectly enhance SEO via increased referral
        traffic, social shares, and potential backlinks. In the 2025 digital
        ecosystem, where Meta's AI-driven feeds prioritize topical relevance,
        strategic hashtag use can elevate visibility by 20-40% for
        keyword-aligned posts, supporting brand authority and E-E-A-T signals
        that influence search engine rankings, while mitigating shadowbanning
        risks from irrelevant or overused tags.
      </p>
      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={"Conduct keyword-specific research using Meta's native search or tools like Hashtagify and RiteTag to identify 5-15 relevant hashtags, blending high-volume (e.g., 1M+ posts) with niche variants of the given keyword for balanced reach (e.g., #DigitalMarketingTips alongside #SEO2025). Incorporate 3-11 hashtags per Instagram post—front-loading in captions or comments—and 1-3 on Facebook for subtlety; ensure natural integration to align with post intent. Track performance via Instagram Insights or Meta Business Suite metrics such as reach and interactions, refining selections quarterly to adapt to trending patterns and sustain engagement-driven SEO benefits."}
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">Generate Your Hashtags</div>

        {/* Radio Button Selection */}
        <div className="mb-3 mt-3">
          <p className="fw-normal mb-2">Choose your input type:</p>
          <Form.Group>
            <div className="d-flex gap-4">
              <Form.Check
                type="radio"
                id="keyword-radio"
                name="inputType"
                value="keyword"
                checked={inputType === "keyword"}
                onChange={handleInputTypeChange}
                label="Keyword"
                className="me-3"
              />
              <Form.Check
                type="radio"
                id="content-radio"
                name="inputType"
                value="content"
                checked={inputType === "content"}
                onChange={handleInputTypeChange}
                label="Content"
              />
            </div>
          </Form.Group>
        </div>

        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              {inputType === "keyword"
                ? "Enter a single keyword to get hashtag suggestions."
                : "Enter your content description to get relevant hashtags."}
            </p>
            <Form.Group>
              <div className="d-flex mt-2">
                <Col sm={6} className="me-2">
                  {inputType === "keyword" ? (
                    <Form.Control
                      type="text"
                      name="inputText"
                      placeholder="Enter keyword (e.g., 'fitness', 'travel', 'food')"
                      value={inputText}
                      onChange={handleInputChange}
                      isInvalid={!!errors.inputText}
                      onBlur={handleBlur}
                      required
                      className="px-2"
                      style={getInputFieldStyle('type6')}
                    />
                  ) : (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="inputText"
                      placeholder="Enter content description (e.g., 'healthy breakfast recipe with avocado and eggs', 'morning workout routine for beginners')"
                      value={inputText}
                      onChange={handleInputChange}
                      isInvalid={!!errors.inputText}
                      onBlur={handleBlur}
                      required
                      className="px-2"
                      style={getTextareaStyle('type6')}
                    />
                  )}
                  {errors.inputText && (
                    <Form.Control.Feedback type="invalid">
                      {errors.inputText}
                    </Form.Control.Feedback>
                  )}
                </Col>
                <div className="col-lg-4 col-md-4 col-sm-12 mb-2 me-1">
                  <div className="d-flex justify-content-start align-items-end">
                    <Button
                      variant="dark"
                      className="me-1"
                      onClick={handleSubmit}
                      disabled={!isFormValid}
                      style={getButtonStyle('type6', 'primary')}
                    >
                      Generate Tags
                    </Button>
                    <Button
                      variant="outline-dark"
                      className="me-1"
                      onClick={handleReset}
                      style={getButtonStyle('type6', 'outline')}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {showSearchItem !== "" && (
          <div>
            <p className="d-flex fw-bold mb-0">
              Hashtag suggestions for
              <span className="search-item">&nbsp;"{showSearchItem}"</span>
            </p>
          </div>
        )}
        {isLoading && <Loader />}
        {isResultGot && <MobileSupportTabBar tabs={tabs} />}
      </div>
      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />
    </div>
  );
}
